import { Link } from "react-router-dom";
import { CheckCircle, Copy, FacebookLogo, LinkedinLogo, XLogo, ArrowsCounterClockwise, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { RESULTS, shareUrl, postText, trackQuiz } from "@/lib/quiz";

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

function ShareButtons({ result }) {
  const url = shareUrl(result);
  const text = postText(result);
  const full = `${text} ${url}`;
  const copy = async () => { trackQuiz(result, "copy"); (await copyText(full)) ? toast.success("Post copied. Paste it into Facebook, LinkedIn or X") : toast.error("Couldn't copy, please select the text above"); };
  const open = async (href, label) => {
    trackQuiz(result, label === "X" ? "x" : label.toLowerCase());
    const ok = await copyText(full);
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
    if (ok) toast.success(`Post text copied. Paste it into your ${label} post`);
  };
  const enc = encodeURIComponent;
  return (
    <div className="space-y-3" data-testid="quiz-share">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">Share your result (no personal numbers)</p>
      <p className="rounded-2xl border border-dashed border-cyan-300 bg-cyan-50/60 dark:bg-cyan-950/30 p-4 text-sm" data-testid="quiz-share-text">{text} <span className="text-cyan-700 dark:text-cyan-400 break-all">{url}</span></p>
      <div className="flex flex-wrap gap-2">
        <Button className="rounded-full" onClick={copy} data-testid="quiz-copy-post-button"><Copy size={16} className="mr-1.5" />Copy post &amp; link</Button>
        <Button variant="outline" className="rounded-full" onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, "Facebook")} data-testid="quiz-share-facebook"><FacebookLogo size={16} weight="fill" className="mr-1.5 text-[#1877F2]" />Facebook</Button>
        <Button variant="outline" className="rounded-full" onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, "LinkedIn")} data-testid="quiz-share-linkedin"><LinkedinLogo size={16} weight="fill" className="mr-1.5 text-[#0A66C2]" />LinkedIn</Button>
        <Button variant="outline" className="rounded-full" onClick={() => open(`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`, "X")} data-testid="quiz-share-x"><XLogo size={16} weight="bold" className="mr-1.5" />X</Button>
      </div>
    </div>
  );
}

export function QuizResult({ result, onRestart }) {
  const { user } = useAuth();
  const r = RESULTS[result];
  return (
    <div className="space-y-6 fade-up" data-testid={`quiz-result-${result}`}>
      <div className="glass p-6 sm:p-8">
        <p className="font-mono-num text-xs uppercase tracking-widest text-cyan-700 dark:text-cyan-400">Your result</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-2 text-grad-head" data-testid="quiz-result-title">{r.title}</h2>
        <p className="text-base text-muted-foreground mt-3">{r.body}</p>
        <ul className="mt-5 space-y-2">
          {r.tips.map((t) => <li key={t} className="flex gap-2 text-sm"><CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />{t}</li>)}
        </ul>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Link to={user ? "/dashboard" : "/login?tab=register"}><Button size="lg" className="rounded-full" data-testid="quiz-get-numbers-button">{user ? "See my exact numbers" : "Get your exact numbers free"}<ArrowRight size={16} className="ml-1.5" /></Button></Link>
          <Button variant="ghost" className="rounded-full" onClick={onRestart} data-testid="quiz-restart-button"><ArrowsCounterClockwise size={16} className="mr-1.5" />Retake</Button>
        </div>
        <p className="font-display text-xl sm:text-2xl font-semibold mt-6 text-emerald-700 dark:text-emerald-400" data-testid="quiz-free-banner">Free &amp; unlimited for everyone</p>
      </div>
      <div className="glass p-6 sm:p-8"><ShareButtons result={result} /></div>
    </div>
  );
}
